import React, { useState, useEffect } from 'react';
import { networkAPI } from '../api';

function Network() {
  const [template, setTemplate] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // UI state
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  
  // Form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [name, setName] = useState('');
  const [event, setEvent] = useState('');
  const [preview, setPreview] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, []);

  useEffect(() => {
    // Update preview when form changes
    updatePreview();
  }, [template, name, event]);

  const loadTemplate = async () => {
    try {
      const response = await networkAPI.getTemplate();
      setTemplate(response.data.template || '');
      setMediaUrl(response.data.media_url || '');
    } catch (err) {
      console.error('Error loading template:', err);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      await networkAPI.saveTemplate({ template, media_url: mediaUrl });
      setSuccess('Template saved successfully!');
      setShowTemplateEditor(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save template');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await networkAPI.uploadFile(formData);
      setMediaUrl(response.data.fileUrl);
      setUploadedFile(response.data);
      setSuccess('File uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to upload file: ' + (err.response?.data?.error || err.message));
      setTimeout(() => setError(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!uploadedFile || !uploadedFile.fileUrl) return;

    try {
      const filename = uploadedFile.fileUrl.split('/').pop();
      await networkAPI.deleteFile(filename);
      setMediaUrl('');
      setUploadedFile(null);
      setSuccess('File removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove file');
      setTimeout(() => setError(''), 3000);
    }
  };

  const updatePreview = () => {
    if (!template) {
      setPreview('');
      return;
    }

    let previewText = template
      .replace(/\{\{name\}\}/g, name || '{{name}}')
      .replace(/\{\{event\}\}/g, event || '{{event}}');
    
    setPreview(previewText);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber) {
      setError('Phone number is required');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      await networkAPI.sendMessage({
        phoneNumber,
        name,
        event,
        mediaUrl
      });

      setSuccess('✅ Message sent successfully!');
      
      // Clear form
      setPhoneNumber('');
      setName('');
      setEvent('');
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Failed to send message: ' + (err.response?.data?.error || err.message));
      setTimeout(() => setError(''), 5000);
    } finally {
      setSending(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await networkAPI.getHistory();
      setHistory(response.data.messages || []);
      setShowHistory(true);
    } catch (err) {
      setError('Failed to load history');
      setTimeout(() => setError(''), 3000);
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = template;
    const before = text.substring(0, start);
    const after = text.substring(end);
    setTemplate(before + variable + after);
    
    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + variable.length;
      textarea.focus();
    }, 0);
  };

  return (
    <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2>🤝 Network Messages</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Send personalized introduction messages with custom templates
      </p>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Template Editor Section - Collapsible */}
      {showTemplateEditor ? (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>📝 Message Template</h3>
            <button 
              onClick={() => setShowTemplateEditor(false)}
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: '24px', 
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Create a template with variables: {'{{name}}'}, {'{{event}}'}
          </p>

          <div style={{ marginBottom: '10px' }}>
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={() => insertVariable('{{name}}')}
              style={{ marginRight: '10px' }}
            >
              + {'{{name}}'}
            </button>
            <button 
              className="btn btn-secondary btn-sm" 
              onClick={() => insertVariable('{{event}}')}
            >
              + {'{{event}}'}
            </button>
          </div>

          <textarea
            id="template-textarea"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Hi {{name}}, I am the co-founder of ImaginorLabs. It was nice meeting you at {{event}}. Please find a brief introduction of our company..."
            rows="6"
            style={{ width: '100%', marginBottom: '15px', fontFamily: 'inherit' }}
          />

          {/* File Upload */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              📎 Attachment (Image, PDF, Video)
            </label>
            <input
              type="file"
              onChange={handleFileUpload}
              accept="image/*,application/pdf,video/*,.doc,.docx"
              disabled={uploading}
              style={{ marginBottom: '10px' }}
            />
            {uploading && <span style={{ color: '#666', marginLeft: '10px' }}>Uploading...</span>}
            
            {uploadedFile && (
              <div style={{ 
                padding: '10px', 
                background: '#f0f0f0', 
                borderRadius: '5px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  📄 {uploadedFile.filename} ({(uploadedFile.size / 1024).toFixed(1)} KB)
                </span>
                <button 
                  className="btn btn-sm" 
                  onClick={handleRemoveFile}
                  style={{ background: '#dc3545', color: 'white' }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <button 
            className="btn btn-primary" 
            onClick={handleSaveTemplate}
          >
            💾 Save Template
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: '20px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowTemplateEditor(true)}
            style={{ width: '100%' }}
          >
            📝 Manage Template
          </button>
        </div>
      )}

      {/* Send Message Form */}
      <div className="card">
        <h3>📤 Send Network Message</h3>
        
        <form onSubmit={handleSendMessage}>
          {/* Show current template */}
          {template && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              background: '#f8f9fa', 
              borderRadius: '5px',
              borderLeft: '4px solid #007bff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                <strong style={{ color: '#007bff' }}>📄 Using Template:</strong>
                <button 
                  type="button"
                  className="btn btn-sm btn-secondary" 
                  onClick={() => setShowTemplateEditor(true)}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                >
                  ✏️ Edit
                </button>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#333', 
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                padding: '10px',
                background: 'white',
                borderRadius: '3px'
              }}>
                {template}
              </div>
              {mediaUrl && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  📎 Has attachment
                </div>
              )}
            </div>
          )}

          {!template && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              background: '#fff3cd', 
              borderRadius: '5px',
              borderLeft: '4px solid #ffc107'
            }}>
              <strong style={{ color: '#856404' }}>⚠️ No template configured</strong>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#856404' }}>
                Please create a template first by clicking "Manage Template" above.
              </p>
            </div>
          )}

          <div className="form-group">
            <label>Phone Number *</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="91XXXXXXXXXX"
              required
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Event</label>
            <input
              type="text"
              value={event}
              onChange={(e) => setEvent(e.target.value)}
              placeholder="Tech Summit 2026"
              style={{ width: '100%' }}
            />
          </div>

          {/* Preview */}
          {preview && (
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              background: '#e8f5e9', 
              borderRadius: '5px',
              borderLeft: '4px solid #4caf50'
            }}>
              <strong style={{ display: 'block', marginBottom: '10px', color: '#2e7d32' }}>
                📱 Message Preview:
              </strong>
              <div style={{ whiteSpace: 'pre-wrap', color: '#333' }}>
                {preview}
              </div>
              {mediaUrl && (
                <div style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                  📎 Attachment: {uploadedFile?.filename || 'File attached'}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={sending || !phoneNumber || !template}
              style={{ flex: 1 }}
            >
              {sending ? 'Sending...' : '📤 Send Message'}
            </button>
            
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={loadHistory}
            >
              📊 History
            </button>
          </div>
        </form>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>📊 Message History</h3>
              <button 
                onClick={() => setShowHistory(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer' 
                }}
              >
                ×
              </button>
            </div>
            
            {history.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666' }}>No messages sent yet</p>
            ) : (
              <div className="table-container">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Phone</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Event</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((msg, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px' }}>{msg.name || '-'}</td>
                        <td style={{ padding: '10px' }}>{msg.phone_number}</td>
                        <td style={{ padding: '10px' }}>{msg.event || '-'}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '3px',
                            fontSize: '12px',
                            background: msg.status === 'sent' ? '#4caf50' : '#f44336',
                            color: 'white'
                          }}>
                            {msg.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {new Date(msg.sent_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .container {
            padding: 10px;
          }
          
          .card {
            padding: 15px;
          }
          
          .form-group {
            margin-bottom: 15px;
          }
          
          button {
            width: 100%;
            margin-bottom: 10px;
          }
          
          .btn-sm {
            width: auto;
            display: inline-block;
          }
        }
      `}</style>
    </div>
  );
}

export default Network;
